/**
 * AnalysisChat.jsx
 *
 * A conversational interface for discussing creative analysis results.
 * Users can ask follow-up questions about the AI recommendations.
 */

import React, { useState, useRef, useEffect } from 'react';
import { Send, Sparkles, RefreshCw, MessageSquare, Lightbulb, ChevronDown, ChevronUp } from 'lucide-react';

// Simple markdown renderer for chat messages
const renderMarkdown = (text) => {
    if (!text) return null;

    const paragraphs = text.split(/\n\n+/);

    return paragraphs.map((paragraph, pIdx) => {
        // Check for bullet lists
        if (paragraph.trim().startsWith('- ') || paragraph.trim().startsWith('* ')) {
            const items = paragraph.split('\n').filter(line => line.trim());
            return (
                <ul key={pIdx} style={{ margin: '0.5em 0', paddingLeft: '1.25em' }}>
                    {items.map((item, idx) => {
                        const text = item.replace(/^[-*]\s*/, '');
                        return <li key={idx} style={{ marginBottom: '0.25em' }}>{processInlineFormatting(text)}</li>;
                    })}
                </ul>
            );
        }

        // Check for numbered lists
        if (/^\d+\.\s/.test(paragraph.trim())) {
            const items = paragraph.split('\n').filter(line => line.trim());
            return (
                <ol key={pIdx} style={{ margin: '0.5em 0', paddingLeft: '1.25em' }}>
                    {items.map((item, idx) => {
                        const text = item.replace(/^\d+\.\s*/, '');
                        return <li key={idx} style={{ marginBottom: '0.25em' }}>{processInlineFormatting(text)}</li>;
                    })}
                </ol>
            );
        }

        const lines = paragraph.split('\n');
        const processedLines = lines.map((line, lIdx) => (
            <span key={lIdx}>
                {processInlineFormatting(line)}
                {lIdx < lines.length - 1 && <br />}
            </span>
        ));

        return (
            <p key={pIdx} style={{ margin: pIdx > 0 ? '0.75em 0 0 0' : '0' }}>
                {processedLines}
            </p>
        );
    });
};

// Process bold, italic, and inline code
const processInlineFormatting = (text) => {
    if (!text) return null;

    // Split by formatting patterns and process
    const parts = [];
    let remaining = text;
    let key = 0;

    while (remaining.length > 0) {
        // Bold **text** or __text__
        const boldMatch = remaining.match(/^(.*?)(\*\*|__)(.+?)\2(.*)$/s);
        if (boldMatch) {
            if (boldMatch[1]) parts.push(<span key={key++}>{boldMatch[1]}</span>);
            parts.push(<strong key={key++}>{boldMatch[3]}</strong>);
            remaining = boldMatch[4];
            continue;
        }

        // Italic *text* or _text_
        const italicMatch = remaining.match(/^(.*?)(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)(.*)$/s);
        if (italicMatch) {
            if (italicMatch[1]) parts.push(<span key={key++}>{italicMatch[1]}</span>);
            parts.push(<em key={key++}>{italicMatch[2]}</em>);
            remaining = italicMatch[3];
            continue;
        }

        // Inline code `text`
        const codeMatch = remaining.match(/^(.*?)`([^`]+)`(.*)$/s);
        if (codeMatch) {
            if (codeMatch[1]) parts.push(<span key={key++}>{codeMatch[1]}</span>);
            parts.push(
                <code key={key++} style={{
                    background: 'var(--color-bg-tertiary)',
                    padding: '0.1em 0.3em',
                    borderRadius: 'var(--radius-sm)',
                    fontSize: '0.9em'
                }}>
                    {codeMatch[2]}
                </code>
            );
            remaining = codeMatch[3];
            continue;
        }

        // No more formatting found
        parts.push(<span key={key++}>{remaining}</span>);
        break;
    }

    return parts.length > 0 ? parts : text;
};

const AnalysisChat = ({
    creative,
    analysis,
    metrics,
    adCopy,
    userId,
    brandId,
    onClose
}) => {
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [isExpanded, setIsExpanded] = useState(true);
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // Suggested questions based on analysis
    const getSuggestedQuestions = () => {
        const questions = [];

        if (analysis?.improvements?.length > 0) {
            const topImprovement = analysis.improvements[0];
            questions.push(`How do I implement "${topImprovement.title}"?`);
        }

        if (analysis?.scores?.callToAction < 6) {
            questions.push("What makes a good CTA for this type of ad?");
        }

        if (analysis?.scores?.visualAppeal < 7) {
            questions.push("What specific visual changes would help most?");
        }

        if (metrics?.ctr < 1.5) {
            questions.push("Why might my CTR be below average?");
        }

        if (metrics?.roas < 2) {
            questions.push("How can I improve my ROAS?");
        }

        if (analysis?.abTestIdeas?.length > 0) {
            questions.push("Which A/B test should I run first?");
        }

        // Always include these
        questions.push("What's the single most impactful change I can make?");
        questions.push("How does this compare to industry benchmarks?");

        return questions.slice(0, 4);
    };

    // Initialize with a welcome message
    useEffect(() => {
        if (analysis && messages.length === 0) {
            const welcomeMessage = {
                id: Date.now(),
                role: 'assistant',
                content: `I've analyzed this creative and found some opportunities for improvement. The overall score is **${analysis.overallScore?.toFixed(1)}/10**.\n\n${analysis.summary}\n\nFeel free to ask me anything about the recommendations, how to implement them, or dive deeper into any aspect of the analysis.`,
                timestamp: new Date()
            };
            setMessages([welcomeMessage]);
        }
    }, [analysis]);

    // Scroll to bottom when messages change
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages]);

    // Focus input when expanded
    useEffect(() => {
        if (isExpanded && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isExpanded]);

    const handleSendMessage = async (messageText = inputValue) => {
        if (!messageText.trim() || isLoading) return;

        const userMessage = {
            id: Date.now(),
            role: 'user',
            content: messageText.trim(),
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputValue('');
        if (inputRef.current) {
            inputRef.current.style.height = 'auto';
        }
        setIsLoading(true);

        try {
            const response = await fetch('http://localhost:3001/api/creatives/chat', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    userId,
                    brandId,
                    creativeId: creative.id,
                    question: messageText.trim(),
                    analysis,
                    metrics,
                    adCopy,
                    conversationHistory: messages.slice(-10).map(m => ({
                        role: m.role,
                        content: m.content
                    }))
                })
            });

            if (!response.ok) {
                throw new Error('Failed to get response');
            }

            const data = await response.json();

            const assistantMessage = {
                id: Date.now() + 1,
                role: 'assistant',
                content: data.response,
                timestamp: new Date()
            };

            setMessages(prev => [...prev, assistantMessage]);
        } catch (error) {
            console.error('Error in chat:', error);
            const errorMessage = {
                id: Date.now() + 1,
                role: 'assistant',
                content: "I'm sorry, I encountered an error processing your question. Please try again.",
                timestamp: new Date(),
                isError: true
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const handleSuggestedQuestion = (question) => {
        handleSendMessage(question);
    };

    if (!analysis) return null;

    return (
        <div style={{
            marginTop: 'var(--space-4)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-lg)',
            overflow: 'hidden',
            background: 'var(--color-bg-primary)'
        }}>
            {/* Header */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--space-3) var(--space-4)',
                    background: 'var(--color-bg-secondary)',
                    cursor: 'pointer',
                    borderBottom: isExpanded ? '1px solid var(--color-border-light)' : 'none'
                }}
                onClick={() => setIsExpanded(!isExpanded)}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <MessageSquare size={18} style={{ color: 'var(--color-primary)' }} />
                    <span style={{ fontWeight: 600 }}>Ask about this analysis</span>
                    {messages.length > 1 && (
                        <span style={{
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-tertiary)',
                            background: 'var(--color-bg-tertiary)',
                            padding: '2px 8px',
                            borderRadius: 'var(--radius-full)'
                        }}>
                            {messages.length} messages
                        </span>
                    )}
                </div>
                {isExpanded ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
            </div>

            {isExpanded && (
                <>
                    {/* Messages Area */}
                    <div style={{
                        height: '400px',
                        overflowY: 'auto',
                        padding: 'var(--space-4)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 'var(--space-3)'
                    }}>
                        {messages.map(message => (
                            <div
                                key={message.id}
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: message.role === 'user' ? 'flex-end' : 'flex-start'
                                }}
                            >
                                <div style={{
                                    maxWidth: '85%',
                                    padding: 'var(--space-3)',
                                    borderRadius: 'var(--radius-lg)',
                                    background: message.role === 'user'
                                        ? 'var(--color-primary)'
                                        : message.isError
                                            ? 'var(--color-error-bg)'
                                            : 'var(--color-bg-secondary)',
                                    color: message.role === 'user'
                                        ? 'white'
                                        : message.isError
                                            ? 'var(--color-error)'
                                            : 'inherit',
                                    fontSize: 'var(--font-size-sm)',
                                    lineHeight: 1.5,
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                }}>
                                    {message.role === 'assistant' ? renderMarkdown(message.content) : message.content}
                                </div>
                            </div>
                        ))}

                        {isLoading && (
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--space-2)',
                                padding: 'var(--space-3)',
                                color: 'var(--color-text-secondary)'
                            }}>
                                <RefreshCw size={16} className="spinning" />
                                <span style={{ fontSize: 'var(--font-size-sm)' }}>Thinking...</span>
                            </div>
                        )}

                        <div ref={messagesEndRef} />
                    </div>

                    {/* Suggested Questions */}
                    {messages.length <= 2 && (
                        <div style={{
                            padding: '0 var(--space-4) var(--space-3)',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 'var(--space-2)'
                        }}>
                            {getSuggestedQuestions().map((question, idx) => (
                                <button
                                    key={idx}
                                    onClick={() => handleSuggestedQuestion(question)}
                                    disabled={isLoading}
                                    style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 'var(--space-1)',
                                        padding: 'var(--space-2) var(--space-3)',
                                        background: 'var(--color-bg-secondary)',
                                        border: '1px solid var(--color-border-light)',
                                        borderRadius: 'var(--radius-full)',
                                        fontSize: 'var(--font-size-xs)',
                                        color: 'var(--color-text-secondary)',
                                        cursor: isLoading ? 'not-allowed' : 'pointer',
                                        transition: 'all 0.15s ease',
                                        opacity: isLoading ? 0.5 : 1
                                    }}
                                    onMouseOver={(e) => {
                                        if (!isLoading) {
                                            e.currentTarget.style.background = 'var(--color-primary-bg)';
                                            e.currentTarget.style.borderColor = 'var(--color-primary)';
                                            e.currentTarget.style.color = 'var(--color-primary)';
                                        }
                                    }}
                                    onMouseOut={(e) => {
                                        e.currentTarget.style.background = 'var(--color-bg-secondary)';
                                        e.currentTarget.style.borderColor = 'var(--color-border-light)';
                                        e.currentTarget.style.color = 'var(--color-text-secondary)';
                                    }}
                                >
                                    <Lightbulb size={12} />
                                    {question}
                                </button>
                            ))}
                        </div>
                    )}

                    {/* Input Area */}
                    <div style={{
                        display: 'flex',
                        gap: 'var(--space-2)',
                        padding: 'var(--space-3) var(--space-4)',
                        borderTop: '1px solid var(--color-border-light)',
                        background: 'var(--color-bg-secondary)'
                    }}>
                        <textarea
                            ref={inputRef}
                            value={inputValue}
                            onChange={(e) => {
                                setInputValue(e.target.value);
                                // Auto-resize textarea
                                e.target.style.height = 'auto';
                                e.target.style.height = Math.min(e.target.scrollHeight, 150) + 'px';
                            }}
                            onKeyDown={handleKeyDown}
                            placeholder="Ask a question about this analysis... (Enter to send, Shift+Enter for new line)"
                            disabled={isLoading}
                            rows={1}
                            style={{
                                flex: 1,
                                padding: 'var(--space-2) var(--space-4)',
                                borderRadius: 'var(--radius-lg)',
                                border: '1px solid var(--color-border)',
                                background: 'var(--color-bg-primary)',
                                color: 'var(--color-text-primary)',
                                fontSize: 'var(--font-size-sm)',
                                outline: 'none',
                                resize: 'none',
                                minHeight: '38px',
                                maxHeight: '150px',
                                lineHeight: '1.5',
                                overflowY: 'auto'
                            }}
                        />
                        <button
                            onClick={() => handleSendMessage()}
                            disabled={!inputValue.trim() || isLoading}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: '40px',
                                height: '40px',
                                borderRadius: 'var(--radius-full)',
                                border: 'none',
                                background: inputValue.trim() && !isLoading
                                    ? 'var(--color-primary)'
                                    : 'var(--color-bg-tertiary)',
                                color: inputValue.trim() && !isLoading
                                    ? 'white'
                                    : 'var(--color-text-tertiary)',
                                cursor: inputValue.trim() && !isLoading ? 'pointer' : 'not-allowed',
                                transition: 'all 0.15s ease'
                            }}
                        >
                            {isLoading ? (
                                <RefreshCw size={18} className="spinning" />
                            ) : (
                                <Send size={18} />
                            )}
                        </button>
                    </div>
                </>
            )}

            <style>{`
                .spinning {
                    animation: spin 1s linear infinite;
                }
                @keyframes spin {
                    from { transform: rotate(0deg); }
                    to { transform: rotate(360deg); }
                }
            `}</style>
        </div>
    );
};

export default AnalysisChat;
